import { useCallback, useEffect, useState } from 'react';
import cloudApi from '../services/cloudApi';

// Three Integrations cards need the same grant list (Claude, ChatGPT, MCP), and
// each mounting its own fetch meant three identical requests and three separate
// spinners resolving at different times. One module-level cache + in-flight
// promise collapses that into a single request, and every subscriber re-renders
// together so a disconnect on one card is reflected on the others.

let cache = null; // null = never loaded
let inFlight = null;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

const load = (force = false) => {
  // Concurrent callers always share one request — three cards mount at once, and
  // on a revalidate they must not each fire their own.
  if (inFlight) return inFlight;
  if (!force && cache) return Promise.resolve(cache);
  inFlight = cloudApi
    .getOAuthGrants()
    // Grants are unavailable when OAuth is off — that's "none connected", not an
    // error worth surfacing on a card whose job is to advertise connecting.
    .then((data) => (Array.isArray(data) ? data : []))
    .catch(() => [])
    .then((grants) => {
      cache = grants;
      inFlight = null;
      return grants;
    });
  return inFlight;
};

/**
 * @param {(grant: object) => boolean} [filter] narrows the list to one client
 */
export const useOAuthGrants = (filter) => {
  const [grants, setGrants] = useState(cache);

  useEffect(() => {
    const sync = () => setGrants(cache);
    listeners.add(sync);
    // Stale-while-revalidate: the cache paints instantly on a return visit, and a
    // background refetch picks up anything connected since (the cache outlives the
    // page, so serving it without revalidating would go stale for the session).
    load(cache !== null).then(emit);
    return () => listeners.delete(sync);
  }, []);

  const reload = useCallback(() => load(true).then(emit), []);

  // Optimistic removal after a revoke — the row disappears everywhere at once
  // instead of waiting on a refetch.
  const removeGrant = useCallback((grantId) => {
    cache = (cache || []).filter((g) => g.id !== grantId);
    emit();
  }, []);

  return {
    grants: grants ? (filter ? grants.filter(filter) : grants) : [],
    loading: grants === null,
    reload,
    removeGrant,
  };
};

export default useOAuthGrants;
